import React, { useState } from 'react'
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Stack } from '@mui/material'
import { Column, Row } from '../../store/types/utils/DataTableUtils'
import { useTranslation } from 'react-i18next'
import CheckboxSelectAll from '../Checkbox/CheckboxSelectAll'
import { ArrowUpward } from '@mui/icons-material'

interface SortingMenuProps {
  onSortChange: (property: string) => void
  currentSortProperty: string
  sortOrder: 'asc' | 'desc'
  sortableFields: Column[]
  setSortOrder: (order: 'asc' | 'desc') => void
  unsortableColumns: Column[] | undefined
  data: Row[]
  selectedRows: Row[]
  setSelected: React.Dispatch<React.SetStateAction<Row[]>>
  renderCheckboxSelectAll: boolean
  singleSelection: boolean
}

const SortingMenu: React.FC<SortingMenuProps> = ({
  singleSelection,
  onSortChange,
  currentSortProperty,
  sortOrder,
  sortableFields,
  setSortOrder,
  unsortableColumns,
  data,
  selectedRows,
  setSelected,
  renderCheckboxSelectAll,
}) => {
  const [sortProperty, setSortProperty] = useState(currentSortProperty)
  const { t } = useTranslation()

  const handlePropertyChange = (event: SelectChangeEvent) => {
    const newProperty = event.target.value
    setSortProperty(newProperty)
    onSortChange(newProperty)
  }

  const handleOrderToggle = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    setSortOrder(newOrder)
    onSortChange(sortProperty)
  }

  const isDataEmpty = data.length === 0

  return (
    <Box
      sx={{
        width: '100%',
        height: 'fit-content',
        backgroundColor: 'background.main',
        top: -33,
        position: 'sticky',
        paddingY: '16px',
        paddingX: '0',
        marginBottom: '1px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 200,
      }}
    >
      <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
        {renderCheckboxSelectAll && !singleSelection && (
          <Box sx={{ paddingLeft: '12px' }}>
            <CheckboxSelectAll data={data} selected={selectedRows} setSelected={setSelected} />
          </Box>
        )}

        <Stack direction="row" alignItems="center" width="100%">
          <FormControl sx={{ minWidth: 150, width: '100%' }} disabled={isDataEmpty} size="small">
            <InputLabel id="sort-by-label">{t('page.sort.sortBy')}</InputLabel>
            <Select labelId="sort-by-label" value={sortProperty} label="Sort By" onChange={handlePropertyChange}>
              {sortableFields.map(field => {
                const isUnsortable = unsortableColumns?.some(col => col.id === field.id)
                if (isUnsortable) {
                  return null
                }
                return (
                  <MenuItem key={field.id} value={field.id}>
                    {field.label}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>

          <IconButton
            onClick={handleOrderToggle}
            sx={{
              transition: 'transform 0.3s',
              transform: sortOrder === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
              marginLeft: '8px',
            }}
            disabled={isDataEmpty}
          >
            {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowUpward />}
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  )
}

export default SortingMenu
